/**
 * Notification Utilities
 * SweetAlert-like notification helpers for consistent notifications across the app
 *
 * Example usage:
 *
 * // Show a delete confirmation dialog (red styled)
 * const confirmed = await showDeleteConfirm('Project Alpha', 'project');
 * if (confirmed) { deleteProject(); }
 *
 * // Show a warning confirmation dialog (amber styled)
 * const confirmed = await showWarningConfirm('Warning', 'This action may affect other records');
 *
 * // Show a custom styled confirmation dialog
 * const confirmed = await showConfirm('Title', 'Message', 'OK', 'Cancel', 'info');
 */

import { toast } from "../../hooks/use-toast"

export interface NotificationOptions {
  title: string
  message?: string
  duration?: number
}

/**
 * Show success notification
 */
export function showSuccess(options: NotificationOptions | string) {
  const { title, message, duration } = typeof options === 'string'
    ? { title: options, message: undefined, duration: 5000 }
    : { ...options, duration: options.duration || 5000 }

  toast({
    variant: "success",
    title,
    description: message,
    duration,
  })
}

/**
 * Show error notification
 */
export function showError(options: NotificationOptions | string) {
  const { title, message, duration } = typeof options === 'string'
    ? { title: options, message: undefined, duration: 7000 }
    : { ...options, duration: options.duration || 7000 }

  toast({
    variant: "error",
    title,
    description: message,
    duration,
  })
}

/**
 * Show warning notification
 */
export function showWarning(options: NotificationOptions | string) {
  const { title, message, duration } = typeof options === 'string'
    ? { title: options, message: undefined, duration: 5000 }
    : { ...options, duration: options.duration || 5000 }

  toast({
    variant: "warning",
    title,
    description: message,
    duration,
  })
}

/**
 * Show info notification
 */
export function showInfo(options: NotificationOptions | string) {
  const { title, message, duration } = typeof options === 'string'
    ? { title: options, message: undefined, duration: 5000 }
    : { ...options, duration: options.duration || 5000 }

  toast({
    variant: "info",
    title,
    description: message,
    duration,
  })
}

/**
 * Show API response notification
 * Automatically determines success/error based on response
 */
export function showApiResponse(
  success: boolean,
  successMessage: string,
  errorMessage: string,
  errorDetails?: string
) {
  if (success) {
    showSuccess(successMessage)
  } else {
    showError({
      title: errorMessage,
      message: errorDetails,
    })
  }
}

/**
 * Notification helper for form submissions
 */
export function showFormSubmitResult(
  success: boolean,
  entityName: string,
  action: 'created' | 'updated' | 'deleted' | 'submitted',
  errorMessage?: string
) {
  if (success) {
    const actionPastTense = {
      created: 'created',
      updated: 'updated',
      deleted: 'deleted',
      submitted: 'submitted'
    }[action]

    showSuccess({
      title: 'Success',
      message: `${entityName} ${actionPastTense} successfully!`,
    })
  } else {
    showError({
      title: `Failed to ${action} ${entityName}`,
      message: errorMessage || 'An error occurred. Please try again.',
    })
  }
}

// Global confirm dialog state management
let globalConfirmResolver: ((value: boolean) => void) | null = null;
let globalConfirmConfig: {
  title: string;
  description: string;
  confirmText: string;
  cancelText: string;
  variant: 'default' | 'destructive' | 'warning' | 'info' | 'success';
  isOpen: boolean;
} = {
  title: '',
  description: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  variant: 'default',
  isOpen: false,
};

let confirmStateListener: ((config: typeof globalConfirmConfig) => void) | null = null;

/**
 * Register confirm dialog state listener
 * This should be called by the global ConfirmDialogProvider
 */
export function registerConfirmDialogListener(
  listener: (config: typeof globalConfirmConfig) => void
) {
  confirmStateListener = listener;
}

/**
 * Show confirmation dialog
 * Returns a promise that resolves to true if confirmed, false if cancelled
 */
export function showConfirm(
  title: string,
  message: string,
  confirmText: string = 'Confirm',
  cancelText: string = 'Cancel',
  variant: 'default' | 'destructive' | 'warning' | 'info' | 'success' = 'default'
): Promise<boolean> {
  return new Promise((resolve) => {
    globalConfirmResolver = resolve;
    globalConfirmConfig = {
      title,
      description: message,
      confirmText,
      cancelText,
      variant,
      isOpen: true,
    };

    if (confirmStateListener) {
      confirmStateListener(globalConfirmConfig);
    }
  });
}

/**
 * Handle confirm dialog response
 * This should be called by the global ConfirmDialogProvider
 */
export function handleConfirmResponse(confirmed: boolean) {
  if (globalConfirmResolver) {
    globalConfirmResolver(confirmed);
    globalConfirmResolver = null;
  }

  globalConfirmConfig = {
    ...globalConfirmConfig,
    isOpen: false,
  };

  if (confirmStateListener) {
    confirmStateListener(globalConfirmConfig);
  }
}

/**
 * Show delete confirmation dialog
 */
export function showDeleteConfirm(
  itemName: string,
  itemType: string = 'item'
): Promise<boolean> {
  return showConfirm(
    `Delete ${itemType}`,
    `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
    'Delete',
    'Cancel',
    'destructive'
  );
}

/**
 * Show warning confirmation dialog
 */
export function showWarningConfirm(
  title: string,
  message: string,
  confirmText: string = 'Continue',
  cancelText: string = 'Cancel'
): Promise<boolean> {
  return showConfirm(title, message, confirmText, cancelText, 'warning');
}

// Export toast for direct usage if needed
export { toast }
